/**
 * Повторная примерка забронированной сделки — вкладка «Договор» «Чатов» и блок сделки расширения:
 * создать, изменить целиком, отменить. Запись — в боевую CRM. Спеки
 * `docs/specs/2026-10-21-deal-visit-design.md`, `docs/specs/2026-10-22-deal-visit-edit-design.md`.
 */
import { apiFetch } from "@/lib/api";

export type VisitCategory = {
  id: number;
  name: string;
  hint: string | null;
  need_product: boolean;
  tailor: boolean;
  /** «Замер Подшива» / «Проверить Подшив» — форма сверяет их с подшивом товара. */
  podshiv?: "measure" | "check" | null;
};
export type VisitReserve = { deal: number; start: string | null; end: string | null };
/** Открытая задача товара договора: «Обещали», «Ремонт», «Химчистка», «Подшив». */
export type VisitTask = {
  id: number;
  task: number;
  name: string;
  status: number;
  status_name: string | null;
  color: string | null;
  text: string | null;
  who: string | null;
  at: string | null;
  /** Фото задачи — у сервера с 22.10.26 (окно задачи в форме). */
  previews?: string[];
  images?: string[];
};
/** Задача «Подшив нужен» товара: нет / не выполнена / выполнена. */
export type VisitPodshiv = {
  state: "none" | "open" | "done";
  status_name?: string | null;
  text?: string | null;
  who?: string | null;
  at?: string | null;
};
/** Порядок — по категориям каталога; задачи, подшив и категория — у нового сервера. */
export type VisitProduct = {
  serial: number;
  name: string;
  photo: string | null;
  in_deal: boolean;
  reserves: VisitReserve[];
  category?: string | null;
  tasks?: VisitTask[];
  podshiv?: VisitPodshiv;
  contract_tailor?: string | null;
};

/** У товара есть задача на подшив — выполненная или нет: только тогда есть что проверять. */
export const hasPodshiv = (p: VisitProduct) => p.podshiv?.state === "open" || p.podshiv?.state === "done";

/** Мастер на повторке нужен у «Замер» / «Проверить подшив» и у категорий, где его требует CRM —
 *  галочки нет, ставим сами (владелец 22.10.26). */
export const needsTailor = (cat: VisitCategory | undefined) => !!cat && (!!cat.podshiv || cat.tailor);

/** Что сотрудник собирается сделать — первый шаг формы, как цели страницы брони my; неизвестная
 *  категория — описание из CRM. */
export const CATEGORY_TEXT: Record<number, string> = {
  1: "Мастер снимет мерку длины подшива — под туфли клиента. Отметьте вещь, которую подшивать.",
  2: "Подшив по задаче готов или в работе — примерить и проверить длину вместе с мастером.",
  3: "Ещё раз примерить вещи из брони — платье или весь наряд.",
  4: "Подобрать ещё вещи к наряду или заменить что-то из брони. Вещи брони отмечать не обязательно.",
  5: "Другая причина — напишите её в комментарии.",
};

/** Подсказка по подшиву: категория «Проверить» — а подшив не сделан; «Замер» — а подшив уже
 *  стоит или сделан. Товар без задачи при «Проверить» — попутный, о нём молчим. Пусто — сходится. */
export function podshivHint(cat: VisitCategory | undefined, p: VisitProduct): string | null {
  const s = p.podshiv;
  if (!cat?.podshiv || !s) return null;
  if (cat.podshiv === "check") {
    // «Не выполнено» повторяло бы саму фразу — уточняем только «в работе».
    const work = s.status_name && !/^не выполн/i.test(s.status_name) ? ` (${s.status_name.toLowerCase()})` : "";
    if (s.state === "open") return `${p.name}: подшив ещё не выполнен${work} — успеет ли мастер к повторке`;
    return null;
  }
  if (s.state === "done") return `${p.name}: подшив уже выполнен — может, «Проверить подшив»`;
  if (s.state === "open") return `${p.name}: задача на подшив уже стоит${s.text ? ` — «${s.text}»` : ""}`;
  return null;
}
export type ActiveVisit = {
  id: number;
  date: string;
  category: number;
  category_name: string | null;
  tailor: boolean;
  comment: string | null;
  serials: number[];
};
/** Повторка сделки любого статуса: 1 запланирована, 2 отменена, 3 прошла, 4 просрочена. */
export type VisitPast = {
  id: number;
  date: string | null;
  status: number;
  status_name: string | null;
  category_name: string | null;
  serials: number[];
};
export type VisitForm = {
  deal: { id: number; booked: boolean; rent_start: string | null; rent_end: string | null; city: string | null; now: string };
  active: ActiveVisit | null;
  /** Новые сверху; у старого сервера поля нет. */
  visits?: VisitPast[];
  categories: VisitCategory[];
  products: VisitProduct[];
};
export type NewVisit = { category: number; date: string; serials: number[]; tailor: boolean; comment: string };
export type VisitSaved = { visit_id: number; date: string; warnings: string[] };

export const loadVisitForm = (dealId: number) => apiFetch<VisitForm>(`/api/deals/${dealId}/visit`);
export const createVisit = (dealId: number, body: NewVisit, chatId?: number) =>
  apiFetch<VisitSaved & { category_name: string; serials: number[] }>(`/api/deals/${dealId}/visit`, {
    method: "POST",
    body: JSON.stringify({ ...body, chat_id: chatId || null }),
  });
export const moveVisit = (dealId: number, visitId: number, date: string, chatId?: number) =>
  apiFetch<VisitSaved & { was: string }>(`/api/deals/${dealId}/visit/${visitId}/date`, {
    method: "POST",
    body: JSON.stringify({ date, chat_id: chatId || null }),
  });

export const editVisit = (dealId: number, visitId: number, body: NewVisit, chatId?: number) =>
  apiFetch<VisitSaved & { category_name: string; serials: number[]; changed: string[] }>(
    `/api/deals/${dealId}/visit/${visitId}`,
    { method: "POST", body: JSON.stringify({ ...body, chat_id: chatId || null }) },
  );
export const cancelVisit = (dealId: number, visitId: number, chatId?: number) =>
  apiFetch<{ visit_id: number; date: string }>(`/api/deals/${dealId}/visit/${visitId}/cancel`, {
    method: "POST",
    body: JSON.stringify({ chat_id: chatId || null }),
  });

/** Последняя повторка товара (новые сверху) — отметка на карточке товара договора. */
export const visitOf = (form: VisitForm, serial: number | null): VisitPast | null =>
  serial === null ? null : (form.visits ?? []).find((v) => v.serials.includes(serial)) ?? null;

const WD = ["вс", "пн", "вт", "ср", "чт", "пт", "сб"];

/** `2026-10-25T17:30` → «вс 25.10 17:30» — без года: повторка всегда в ближайшие недели. */
export function visitWhen(value: string | null): string {
  if (!value || !/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(value)) return "—";
  const d = new Date(Number(value.slice(0, 4)), Number(value.slice(5, 7)) - 1, Number(value.slice(8, 10)));
  return `${WD[d.getDay()]} ${value.slice(8, 10)}.${value.slice(5, 7)} ${value.slice(11, 16)}`;
}

const DAY = 86_400_000;

/** Полночь дня `ГГГГ-ММ-ДД` в мс по местным часам браузера — сравниваем только дни и часы
 *  салона, пояса браузера в расчёте нет (оба конца считаются одинаково). */
const midnight = (day: string) => new Date(`${day.slice(0, 10)}T00:00`).getTime();

/** Чужие брони товара, задетые повтором: `[начало − 1 день, конец + 1 день)` по началу суток —
 *  как на сервере и в crm-back. */
export function busyWith(product: VisitProduct, date: string): VisitReserve[] {
  if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(date)) return [];
  const at = new Date(date.slice(0, 16)).getTime();
  return product.reserves.filter((r) => {
    const start = r.start ?? r.end;
    const end = r.end ?? r.start;
    if (!start || !end) return false;
    return midnight(start) - DAY <= at && at < midnight(end) + DAY;
  });
}

/** Сб/вс, час 12–17 — правило CRM `visitWeekendWrong`. */
export function weekendRush(date: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(date)) return false;
  const d = new Date(date.slice(0, 16));
  const h = d.getHours();
  return (d.getDay() === 0 || d.getDay() === 6) && h >= 12 && h <= 17;
}

/** `2026-10-28` → «28.10». */
export const ddmm = (day: string | null) => (day ? `${day.slice(8, 10)}.${day.slice(5, 7)}` : "—");
